import type { Task } from '../types/task.js'

// Define a more permissive constructor type
type TaskConstructor = {
  new(...args: never[]): Task
}

/**
 * TaskRegistry manages task implementation classes
 * It provides a central registry for looking up task classes by name
 */
const TaskRegistry = {
  // Private storage for task implementations
  implementations: {} as Record<string, TaskConstructor>,

  /**
   * Register a task implementation class
   * @param name The name to register the implementation under
   * @param implementation The task class constructor
   */
  register(name: string, implementation: TaskConstructor): void {
    this.implementations[name] = implementation
  },

  /**
   * Get a task implementation class by name
   * @param name The registered name of the task implementation
   * @returns The task class constructor
   */
  get(name: string): TaskConstructor {
    const impl = this.implementations[name]
    if (!impl) {
      throw new Error(`Task implementation not found: ${name}`)
    }
    return impl
  },

  /**
   * Check if a task implementation is registered
   * @param name The name to check
   * @returns True if the implementation is registered
   */
  has(name: string): boolean {
    return !!this.implementations[name]
  },

  /**
   * Get all registered task implementations
   * @returns Map of implementation names to constructors
   */
  getAll(): Record<string, TaskConstructor> {
    return { ...this.implementations }
  },
}

export { TaskRegistry }
